import type {
  ActiveConnection,
  CredentialHealth,
  ConnectedNode,
  DashboardRecommendation,
  DashboardSession,
  DashboardSummary,
  DiagnosticsEndpoint,
  RelayEndpoint,
  RelayPair,
  StickyAssignment,
  NodeCategory,
  NodeStatus
} from "../utils/dashboard-types"

const now = Date.UTC(2026, 5, 28, 0, 0, 0)

const countries = [
  ["US", "Austin", "TX", "America/Chicago", "AS7922", "Comcast Cable", "iad"],
  ["GB", "London", "LND", "Europe/London", "AS5089", "Virgin Media", "lhr"],
  ["DE", "Berlin", "BE", "Europe/Berlin", "AS3320", "Deutsche Telekom", "fra"],
  ["JP", "Tokyo", "13", "Asia/Tokyo", "AS2516", "KDDI", "nrt"],
  ["SG", "Singapore", "SG-01", "Asia/Singapore", "AS9506", "Singtel", "sin"],
  ["BR", "Sao Paulo", "SP", "America/Sao_Paulo", "AS28573", "Claro", "gru"],
  ["AU", "Sydney", "NSW", "Australia/Sydney", "AS4764", "Aussie Broadband", "syd"],
  ["CA", "Toronto", "ON", "America/Toronto", "AS812", "Rogers Cable", "yyz"]
] as const

const categories: NodeCategory[] = ["android-sdk", "residential-isp", "vps"]
const statuses: NodeStatus[] = ["available", "available", "available", "draining", "stale", "offline"]
const targets = [
  "api.accounts.example",
  "checkout.partner.example",
  "media.lookup.example",
  "os.undetect.io",
  "reports.vendor.example",
  "relay.health.example"
]

function fixtureDeviceId(index: number): string {
  const firstSegment = (0x4e4f4400 + index + 1).toString(16).padStart(8, "0")
  const lastSegment = (0x100000000000 + index + 1).toString(16).slice(-12)
  return `${firstSegment}-7c3a-4f0b-9d12-${lastSegment}`
}

export const connectedNodes: ConnectedNode[] = Array.from({ length: 240 }, (_, index) => {
  const [country, city, regionCode, timezone, asn, isp, relayRegion] = countries[index % countries.length]
  const nodeCategory = categories[index % categories.length]
  const status = statuses[index % statuses.length]
  const maxClients = 5
  const activeClients = status === "offline" ? 0 : (index * 7) % (maxClients + 1)

  return {
    activeClients,
    activeChannels: activeClients * 3,
    acceptingClients: status === "available" && activeClients < maxClients,
    asn,
    capacityReason:
      status === "offline"
        ? "disconnected"
        : activeClients >= maxClients
          ? "full"
          : status === "draining"
            ? "draining"
            : "available",
    charging: nodeCategory === "android-sdk" ? index % 5 !== 0 : true,
    city,
    country,
    deviceId: fixtureDeviceId(index),
    id: `node-${nodeCategory}-${String(index + 1).padStart(3, "0")}`,
    ipFamily: index % 4 === 0 ? "Dual" : index % 2 === 0 ? "IPv6" : "IPv4",
    isp,
    lastSeenAt: now - ((index % 36) + 1) * 45_000,
    latencyMs: 18 + ((index * 11) % 96),
    maxClients,
    nodeCategory,
    observedIpAddress: `198.51.${100 + Math.floor(index / 200)}.${10 + (index % 200)}`,
    regionCode,
    relayRegion,
    sdkVersion: `1.${10 + (index % 4)}.${index % 9}`,
    status,
    throughputMbps: 32 + ((index * 13) % 480),
    timezone,
    transport: "relay-masque"
  }
})

export const activeConnections: ActiveConnection[] = Array.from({ length: 420 }, (_, index) => {
  const node = connectedNodes[index % connectedNodes.length]
  const durationSeconds = 180 + ((index * 71) % 24_000)

  return {
    clientId: `client-${String((index % 180) + 1).padStart(3, "0")}`,
    country: node.country,
    durationSeconds,
    edgeRegion: node.relayRegion,
    id: `conn-${String(index + 1).padStart(4, "0")}`,
    nodeId: node.id,
    protocol: index % 9 === 0 ? "udp" : "tcp",
    rxBytes: 240_000 + index * 91_337,
    sessionId: `ses_${String((index % 80) + 1).padStart(3, "0")}`,
    startedAt: now - durationSeconds * 1000,
    status: index % 13 === 0 ? "paused" : index % 5 === 0 ? "warming" : "active",
    sticky: index % 4 !== 0,
    strict: index % 3 === 0,
    target: targets[index % targets.length],
    txBytes: 180_000 + index * 77_009
  }
})

export const dashboardSessions: DashboardSession[] = Array.from({ length: 48 }, (_, index) => {
  const node = connectedNodes[index % connectedNodes.length]
  const isDesktopProfile = node.nodeCategory !== "android-sdk" && index % 4 === 0

  return {
    activeCredentialKid: `kid-${String((index % 6) + 1).padStart(2, "0")}`,
    appId: index % 3 === 0 ? "sdk-debugger" : "browser-client",
    closedAt: index % 11 === 0 ? now - ((index % 5) + 1) * 610_000 : undefined,
    createdAt: now - (index + 2) * 1_340_000,
    credentialExpiresAt: now + ((index % 8) + 2) * 300_000,
    id: `ses_${String(index + 1).padStart(3, "0")}`,
    lastSeenAt: now - ((index % 18) + 1) * 58_000,
    previousKidGraceSeconds: index % 5 === 0 ? 90 : 30,
    requestedCountry: node.country,
    requestedProfile: isDesktopProfile ? (index % 2 === 0 ? "windows" : "macos") : undefined,
    strict: index % 3 === 0,
    stickyHashPresent: index % 4 !== 0
  }
})

const credentialExpirations = dashboardSessions
  .map((session) => session.credentialExpiresAt)
  .filter((expiresAt): expiresAt is number => expiresAt !== null)

export const credentialHealth: CredentialHealth = {
  activeCredentials: dashboardSessions.filter((session) => !session.closedAt).length,
  earliestExpiresAt: credentialExpirations.length === 0 ? null : Math.min(...credentialExpirations),
  expiringWithin20Minutes: credentialExpirations.filter((expiresAt) => expiresAt - now < 20 * 60_000).length,
  refreshEvents: 6,
  revokedCredentials: 0,
  totalCredentials: dashboardSessions.length
}

export const stickyAssignments: StickyAssignment[] = dashboardSessions.slice(0, 14).map((session, index) => {
  const node = connectedNodes[(index * 13) % connectedNodes.length]
  const stickyState = index % 6 === 0 ? "fallback" : index % 4 === 0 ? "miss" : "hit"

  return {
    fallbackReason:
      stickyState === "fallback"
        ? "Original node stopped accepting work after charging gate changed."
        : "No fallback required.",
    id: `sticky-${String(index + 1).padStart(2, "0")}`,
    nodeId: node.id,
    requestedCountry: session.requestedCountry,
    selectedReason:
      stickyState === "hit"
        ? "Sticky hash matched an available relay-backed node."
        : "Broker selected nearest available node in requested country.",
    sessionId: session.id,
    strictFailureReason:
      session.strict && stickyState === "fallback" ? "Strict pool had no charging Android nodes." : "",
    stickyState
  }
})

export const relayEndpoints: RelayEndpoint[] = [
  {
    cloud: "vultr",
    datagramsPerSecond: 1842,
    healthPort: 8080,
    id: "relay-iad-01",
    listenPort: 3478,
    maxDatagramBytes: 1200,
    region: "iad",
    sessionTtlSeconds: 90,
    status: "healthy"
  },
  {
    cloud: "vultr",
    datagramsPerSecond: 1288,
    healthPort: 8080,
    id: "relay-lhr-01",
    listenPort: 3478,
    maxDatagramBytes: 1200,
    region: "lhr",
    sessionTtlSeconds: 90,
    status: "healthy"
  },
  {
    cloud: "vultr",
    datagramsPerSecond: 934,
    healthPort: 8080,
    id: "relay-syd-01",
    listenPort: 3478,
    maxDatagramBytes: 1200,
    region: "syd",
    sessionTtlSeconds: 90,
    status: "degraded"
  }
]

export const relayPairs: RelayPair[] = dashboardSessions.slice(0, 36).map((session, index) => {
  const status = index % 12 === 0 ? "idle-expiring" : index % 9 === 0 ? "unpaired" : "paired"

  return {
    androidState: status === "unpaired" ? "missing" : "connected",
    browserState: index % 14 === 0 ? "replaced" : "connected",
    closeReason: status === "paired" ? "open" : status === "unpaired" ? "counterpart missing" : "idle timeout pending",
    forwardErrors: index % 9,
    idleSeconds: 4 + ((index * 7) % 86),
    latencyMs: 21 + ((index * 17) % 134),
    lossPercent: Number(((index % 7) * 0.18).toFixed(2)),
    receivedBytes: 2_400_000 + index * 390_111,
    relayRegion: relayEndpoints[index % relayEndpoints.length].region,
    sentBytes: 2_100_000 + index * 341_903,
    sessionId: session.id,
    status
  }
})

const summaryRecentEvents: DashboardSummary["recentEvents"] = [
  {
    component: "broker",
    id: "event-001",
    level: "info",
    message: "Assigned strict session ses_018 to node-android-sdk-019",
    sessionId: "ses_018",
    nodeId: "node-android-sdk-019",
    timestamp: now - 72_000,
    type: "client_connected"
  },
  {
    component: "smart-switching",
    id: "event-002",
    level: "warn",
    message: "Node node-android-sdk-006 stopped accepting new client work",
    nodeId: "node-android-sdk-006",
    timestamp: now - 142_000,
    type: "smart_switching_state"
  },
  {
    component: "events",
    id: "event-003",
    level: "info",
    message: "Uploaded observability batch for 36 client events",
    timestamp: now - 214_000,
    type: "transport_metric"
  },
  {
    component: "relay",
    id: "event-004",
    level: "info",
    message: "Sydney relay region reported healthy UDP forwarding",
    timestamp: now - 329_000,
    type: "transport_metric"
  }
]

export const eventStream = [
  ...summaryRecentEvents,
  {
    clientId: "client-044",
    component: "relay",
    id: "event-005",
    level: "warn" as const,
    message: "Relay pair for ses_012 replaced browser participant after reconnect.",
    sessionId: "ses_012",
    timestamp: now - 421_000,
    type: "transport_metric"
  },
  {
    component: "credentials",
    id: "event-006",
    level: "info" as const,
    message: "Credential kid-04 entered previous kid grace window for ses_031.",
    sessionId: "ses_031",
    timestamp: now - 506_000,
    type: "credential_refreshed"
  },
  {
    component: "worker",
    id: "event-007",
    level: "error" as const,
    message: "Rejected one event batch with an unsupported detail schema.",
    timestamp: now - 612_000,
    type: "emit_failed"
  },
  {
    component: "browser-client",
    id: "event-008",
    level: "warn" as const,
    message: "SOCKS UDP association opened in no-Chrome staging check.",
    sessionId: "ses_021",
    timestamp: now - 771_000,
    type: "transport_metric"
  }
]

export const dashboardRecommendations: DashboardRecommendation[] = [
  {
    affectedCount: 18,
    evidence: "Android charging eligibility dropped to 92% and one strict fallback appeared in the last window.",
    id: "rec-charging",
    nextAction: "Inspect nodes that are available but not charging before increasing strict assignment load.",
    reason: "Charging gate is the first capacity limiter for Android SDK nodes.",
    scope: "android-sdk",
    severity: "high",
    title: "Charging gate is reducing strict pool capacity"
  },
  {
    affectedCount: 7,
    evidence: "Sydney relay has elevated idle-expiring pairs and 1.08% packet loss estimate.",
    id: "rec-relay-syd",
    nextAction: "Compare syd relay counters with lhr/iad and keep strict sessions away from syd if degradation grows.",
    reason: "Relay pair quality is lower in one region while controlplane health still looks normal.",
    scope: "relay:syd",
    severity: "medium",
    title: "Sydney relay metrics need review"
  },
  {
    affectedCount: 1,
    evidence: "One event batch was rejected because an emitted detail schema was not recognized.",
    id: "rec-event-schema",
    nextAction: "Align the SDK event detail schema with the Worker ingest contract.",
    reason: "Rejected telemetry hides dataplane evidence that operators need for diagnosis.",
    scope: "events",
    severity: "low",
    title: "Telemetry detail schema needs one cleanup"
  }
]

export const diagnosticsEndpoints: DiagnosticsEndpoint[] = [
  { id: "snapshot", label: "Snapshot", path: "/v1/data/snapshot", status: "ready" },
  { id: "summary", label: "Summary", path: "/v1/data/summary", status: "ready" },
  { id: "nodes", label: "Nodes", path: "/v1/data/nodes.json", status: "ready" },
  { id: "connections", label: "Connections", path: "/v1/data/connections.json", status: "ready" },
  { id: "sessions", label: "Sessions", path: "/v1/data/sessions.json", status: "planned" },
  { id: "relay", label: "Relay", path: "/v1/data/relay.json", status: "planned" },
  { id: "events", label: "Events", path: "/v1/data/events.json", status: "planned" },
  { id: "recommendations", label: "Recommendations", path: "/v1/data/recommendations.json", status: "planned" }
]

const availableNodeCount = connectedNodes.filter((node) => node.status === "available").length
const drainingNodeCount = connectedNodes.filter((node) => node.status === "draining").length
const staleNodeCount = connectedNodes.filter((node) => node.status === "stale").length
const offlineNodeCount = connectedNodes.filter((node) => node.status === "offline").length
const openSessionCount = dashboardSessions.filter((session) => !session.closedAt).length

export const dashboardSummary: DashboardSummary = {
  generatedAt: now,
  panels: [
    {
      detail: `${drainingNodeCount} draining, ${staleNodeCount} stale, and ${offlineNodeCount} disconnected records in the fixture scenario.`,
      id: "nodes",
      progress: Math.round((availableNodeCount / connectedNodes.length) * 100),
      title: "Node registrations",
      tone: "success",
      value: `${availableNodeCount} available`
    },
    {
      detail: `${dashboardSessions.length - openSessionCount} closed records retained in the fixture scenario.`,
      id: "sessions",
      progress: Math.round((openSessionCount / dashboardSessions.length) * 100),
      title: "Sessions",
      tone: "neutral",
      value: `${openSessionCount} open`
    },
    {
      detail: `${eventStream.length} fixture events are available for visual testing.`,
      id: "event-ingest",
      progress: 88,
      title: "Event ingest",
      tone: "success",
      value: "1 batch"
    }
  ],
  recentEvents: summaryRecentEvents,
  stats: [
    {
      caption: "Ready for broker assignments",
      id: "active-nodes",
      label: "Active nodes",
      tone: "success",
      trend: `${staleNodeCount} stale`,
      value: `${availableNodeCount}`
    },
    {
      caption: "Recent lifecycle rows",
      id: "active-connections",
      label: "Connection activity",
      tone: "neutral",
      trend: `${activeConnections.filter((connection) => connection.status === "paused").length} paused`,
      value: `${activeConnections.filter((connection) => connection.status === "active").length}`
    },
    {
      caption: "Across available nodes",
      id: "median-latency",
      label: "Median latency",
      tone: "success",
      trend: "fixture median",
      value: "47 ms"
    },
    {
      caption: "Eligible SDK devices",
      id: "charging",
      label: "Charging Android",
      tone: "success",
      trend: "92% of Android nodes",
      value: `${connectedNodes.filter((node) => node.nodeCategory === "android-sdk" && node.charging).length}`
    }
  ]
}

export const emptySummary: DashboardSummary = {
  generatedAt: now,
  panels: [
    {
      detail: "The fixture state is intentionally empty.",
      id: "empty",
      progress: 0,
      title: "No records",
      tone: "neutral",
      value: "Empty"
    }
  ],
  recentEvents: [],
  stats: dashboardSummary.stats.map((stat) => ({
    ...stat,
    trend: "No fixture records",
    value: "0"
  }))
}
